import { InteractionType } from '@azure/msal-browser';
import { getGraphClient } from '@/components/ms-teams/GraphBase';
import { StorageUtils } from '@/components/ms-teams/StorageUtils';
import { getMSALInstance } from '@/components/ms-teams/MSAL';
import { msalConfig } from '@/components/ms-teams/configuration';

const API = {
  RESPONSE: {
    HEADERS: { WWW_AUTHENTICATE: 'www-authenticate' },
    TYPES: { RAW: 'raw' }
  },
  ERRORS: {
    CLAIMS_CHALLENGE: 'claims_challenge_occurred'
  }
};

const genericRequest = async (httpAction, username, scopes, uri, interactionType, msalInstance, payload) => {
  const account = msalInstance.getAccountByUsername(username);
  try {
    const response = await getGraphClient({ account, scopes, interactionType })
      .api(uri)
      .responseType(API.RESPONSE.TYPES.RAW)
      [httpAction](payload);

    await handleClaimsChallenge(account, scopes, response, uri);

    if (response && response.error === API.ERRORS.CLAIMS_CHALLENGE) {
      throw response.error;
    }

    return response;
  } catch (error) {
    if (error === API.ERRORS.CLAIMS_CHALLENGE) {
      await handleClaimsChallengeError(account, scopes, uri, interactionType, error);
    } else if (error.toString().includes('404')) {
      console.log(`GraphService ${httpAction} 404`, { uri });
    } else {
      console.log(`GraphService ${httpAction} error`, { error });
    }
  }
};

const get = (username, scopes, uri, interactionType, msalInstance) =>
  genericRequest('get', username, scopes, uri, interactionType, msalInstance);

const post = (username, scopes, uri, interactionType, msalInstance, payload) =>
  genericRequest('post', username, scopes, uri, interactionType, msalInstance, payload);

const remove = (username, scopes, uri, interactionType, msalInstance) =>
  genericRequest('delete', username, scopes, uri, interactionType, msalInstance);

const handleClaimsChallengeError = async (account, scopes, uri, interactionType, error) => {
  const resource = new URL(uri).hostname;
  console.log('Handling claims challenge error', msalConfig);
  const claims =
    account && StorageUtils.getClaims(`cc.${msalConfig.auth.clientId}.${account.idTokenClaims.oid}.${resource}`)
      ? window.atob(StorageUtils.getClaims(`cc.${msalConfig.auth.clientId}.${account.idTokenClaims.oid}.${resource}`))
      : undefined;
  const request = {
    account,
    scopes,
    claims
  };

  switch (interactionType) {
    case InteractionType.Popup:
      await getMSALInstance().acquireTokenPopup({ ...request, redirectUri: '/redirect' });
      break;
    case InteractionType.Redirect:
      await getMSALInstance().acquireTokenRedirect(request);
      break;
    default:
      await getMSALInstance().acquireTokenRedirect(request);
      break;
  }
};

/**
 * This method inspects the HTTPS response from a fetch call for the "www-authenticate header"
 * If present, it grabs the claims challenge from the header and store it in the localStorage
 * @param account
 * @param scopes
 * @param {object} response
 * @param apiEndpoint
 * @returns response
 */
const handleClaimsChallenge = (account, scopes, response, apiEndpoint) => {
  if (response.status === 200) {
    return response.json();
  } else if (response.status === 401) {
    if (response.headers.get(API.RESPONSE.HEADERS.WWW_AUTHENTICATE)) {
      const authenticateHeader = response.headers.get(API.RESPONSE.HEADERS.WWW_AUTHENTICATE);
      const claimsChallenge = parseChallenges(authenticateHeader);

      console.log('Claims challenge occurred', msalConfig);
      /**
       * This method stores the claim challenge to the session storage in the browser to be used when acquiring a token.
       * To ensure that we are fetching the correct claim from the storage, we are using the clientId
       * of the application and oid (user’s object id) as the key identifier of the claim with schema
       * cc.<clientId>.<oid>.<resource.hostname>
       */
      StorageUtils.addClaims(
        claimsChallenge.claims,
        `cc.${msalConfig.auth.clientId}.${account.idTokenClaims.oid}.${new URL(apiEndpoint).hostname}`
      );

      return { error: API.ERRORS.CLAIMS_CHALLENGE, payload: claimsChallenge.claims };
    }

    throw new Error(`Unauthorized: ${response.status}`);
  } else {
    throw new Error(`Something went wrong with the request: ${response.status}`);
  }
};

/**
 * This method parses WWW-Authenticate authentication headers
 * @param header
 * @return {Object} challengeMap
 */
const parseChallenges = header => {
  const schemeSeparator = header.indexOf(' ');
  const challenges = header.substring(schemeSeparator + 1).split(', ');
  const challengeMap = {};

  challenges.forEach(challenge => {
    const [key, value] = challenge.split('=');
    challengeMap[key.trim()] = window.decodeURI(value.replace(/(^"|"$)/g, ''));
  });

  return challengeMap;
};

export const GraphService = { get, post, remove };
